import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Copy, RefreshCw, Loader2, Building2, Users, Mail, CreditCard, Send, Clock, CheckCircle2, Settings as Cog, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Panel, PersonAvatar } from "@/components/common";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { useAuth, isAdmin, isManager } from "@/context/AuthContext";

const COMMON_TZ = [
  "UTC", "America/Los_Angeles", "America/Denver", "America/Chicago", "America/New_York",
  "America/Toronto", "America/Sao_Paulo", "Europe/London", "Europe/Paris", "Europe/Berlin",
  "Europe/Madrid", "Africa/Cairo", "Africa/Johannesburg", "Asia/Dubai", "Asia/Kolkata",
  "Asia/Singapore", "Asia/Tokyo", "Asia/Shanghai", "Australia/Sydney", "Pacific/Auckland",
];

export default function Settings() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "company";
  const setTab = (v) => setParams((p) => { p.set("tab", v); return p; }, { replace: true });

  return (
    <div data-testid="settings-page">
      <PageHeader title="Settings & team" subtitle="Your company profile, team, briefing email and billing." />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="company" data-testid="settings-tab-company"><Building2 className="h-4 w-4 mr-1.5" />Company</TabsTrigger>
          <TabsTrigger value="briefing" data-testid="settings-tab-briefing"><Mail className="h-4 w-4 mr-1.5" />Briefing email</TabsTrigger>
          <TabsTrigger value="billing" data-testid="settings-tab-billing"><CreditCard className="h-4 w-4 mr-1.5" />Billing</TabsTrigger>
        </TabsList>
        <TabsContent value="company" className="mt-5"><CompanyTab /></TabsContent>
        <TabsContent value="briefing" className="mt-5"><BriefingTab /></TabsContent>
        <TabsContent value="billing" className="mt-5"><BillingTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function CompanyTab() {
  const { user, refresh } = useAuth();
  const admin = isAdmin(user);
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", industry: "", size: "", description: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/companies/me");
      setData(data);
      setForm({ name: data.company.name, industry: data.company.industry || "", size: data.company.size || "", description: data.company.description || "" });
    } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.patch("/companies/me", form); toast.success("Company profile saved"); refresh(); load(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const regen = async () => { try { await api.post("/companies/me/invite-code"); toast.success("New invite code created"); load(); } catch (err) { toast.error(errMsg(err)); } };
  const copy = async () => { try { await navigator.clipboard.writeText(data.company.invite_code); toast.success("Invite code copied"); } catch { toast.error("Copy failed"); } };
  const setRole = async (m, role) => { try { await api.patch(`/companies/members/${m.user_id}`, { role }); toast.success(`${m.name} is now ${role}`); load(); } catch (err) { toast.error(errMsg(err)); } };

  if (!data) return <div className="p-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
      <Panel title="Company profile" icon={Building2} className="lg:col-span-7" testid="settings-company-panel">
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-1.5"><Label>Company name</Label><Input disabled={!admin} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="settings-company-name-input" /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Industry</Label><Input disabled={!admin} value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} data-testid="settings-industry-input" /></div>
            <div className="space-y-1.5"><Label>Size</Label><Input disabled={!admin} value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} data-testid="settings-size-input" /></div>
          </div>
          <div className="space-y-1.5"><Label>About the business</Label><Textarea disabled={!admin} rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="settings-description-input" /></div>
          {admin && <Button type="submit" disabled={busy} data-testid="settings-save-button">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}Save profile</Button>}
        </form>
      </Panel>
      <Panel title="Invite your team" icon={Users} className="lg:col-span-5 h-fit" testid="settings-invite-panel">
        {isManager(user) && data.company.invite_code ? (<>
          <p className="text-sm text-muted-foreground">Share this code. New members sign in, choose &ldquo;Join with code&rdquo; and start as employees.</p>
          <div className="mt-4 flex gap-2">
            <Input readOnly value={data.company.invite_code} className="font-mono text-lg tracking-[0.3em] text-center" data-testid="settings-invite-code" />
            <Button variant="secondary" size="icon" onClick={copy} aria-label="Copy invite code" data-testid="settings-invite-code-copy-button"><Copy className="h-4 w-4" /></Button>
          </div>
          {admin && <Button variant="ghost" size="sm" className="mt-3 px-0" onClick={regen} data-testid="settings-invite-code-regenerate-button"><RefreshCw className="h-3.5 w-3.5 mr-1.5" />Create new code (the old one stops working)</Button>}
        </>) : <p className="text-sm text-muted-foreground">Ask a manager for the invite code.</p>}
      </Panel>
      <Panel title={`Members (${data.members.length})`} icon={Users} className="lg:col-span-12" testid="settings-members-panel">
        <ul className="divide-y divide-border">
          {data.members.map((m) => (
            <li key={m.user_id} className="py-3 flex items-center gap-3" data-testid="settings-member-row">
              <PersonAvatar name={m.name} src={m.picture} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{m.name}{m.user_id === user.user_id && <span className="text-muted-foreground font-normal"> (you)</span>}</p>
                <p className="text-xs text-muted-foreground truncate">{m.email} · {m.auth_provider === "google" ? "Google" : "Email"}</p>
              </div>
              {admin && m.role !== "owner" && m.user_id !== user.user_id ? (
                <Select value={m.role} onValueChange={(v) => setRole(m, v)}>
                  <SelectTrigger className="w-32" data-testid="settings-member-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="admin">Admin</SelectItem><SelectItem value="manager">Manager</SelectItem><SelectItem value="employee">Employee</SelectItem></SelectContent>
                </Select>
              ) : <span className="text-xs capitalize bg-muted rounded-full px-2.5 py-1" data-testid="settings-member-role">{m.role}</span>}
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}

function BriefingTab() {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [data, setData] = useState(null);
  const [tz, setTz] = useState("UTC");
  const [enabled, setEnabled] = useState(true);
  const [sections, setSections] = useState({});
  const [tone, setTone] = useState("concise");
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/briefing-email/settings");
      setData(data); setTz(data.timezone); setEnabled(data.enabled);
      setSections(data.sections || {}); setTone(data.tone || "concise");
    } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSavingPrefs(true);
    try { await api.patch("/briefing-email/settings", { timezone: tz, enabled, sections, tone }); toast.success("Briefing settings saved"); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setSavingPrefs(false); }
  };
  const sendTest = async () => {
    setTesting(true);
    try { const { data } = await api.post("/briefing-email/test"); toast.success(`Test briefing sent to ${data.to}`); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setTesting(false); }
  };
  const setMyPref = async (receive) => { try { await api.patch("/briefing-email/me", { receive }); toast.success(receive ? "You'll get the briefing" : "You'll stop getting it"); load(); } catch (e) { toast.error(errMsg(e)); } };

  if (!data) return <div className="p-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
      <Panel title="Morning briefing email" icon={Mail} className="lg:col-span-7" testid="settings-briefing-panel">
        <div className="space-y-5">
          <div className="flex items-start justify-between gap-4 bg-muted/50 rounded-lg p-3">
            <div>
              <p className="text-sm font-medium">Send every morning at 7 AM</p>
              <p className="text-xs text-muted-foreground mt-0.5">Delivered to owners and admins in your company's local time.</p>
            </div>
            {admin ? (
              <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="settings-briefing-enabled-switch" />
            ) : (
              <Badge variant="outline" className="font-normal">{enabled ? "On" : "Off"}</Badge>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Company time zone</Label>
            <Select value={tz} onValueChange={setTz} disabled={!admin}>
              <SelectTrigger data-testid="settings-briefing-tz-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {COMMON_TZ.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                {!COMMON_TZ.includes(tz) && <SelectItem value={tz}>{tz}</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>What should the email include?</Label>
            <div className="mt-2 space-y-2">
              {(data.available_sections || []).map((s) => (
                <label key={s.key} className="flex items-start gap-3 bg-muted/30 rounded-lg px-3 py-2.5 cursor-pointer" data-testid={`settings-briefing-section-${s.key}`}>
                  <Checkbox checked={!!sections[s.key]} onCheckedChange={(v) => setSections({ ...sections, [s.key]: !!v })} disabled={!admin} className="mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{s.label}</p>
                    <p className="text-xs text-muted-foreground">{s.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>CEO briefing length</Label>
            <Select value={tone} onValueChange={setTone} disabled={!admin}>
              <SelectTrigger data-testid="settings-briefing-tone-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="concise">Concise (under 220 words)</SelectItem>
                <SelectItem value="detailed">Detailed (up to 500 words)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {admin && (
            <div className="flex items-center gap-2 pt-2">
              <Button onClick={save} disabled={savingPrefs} data-testid="settings-briefing-save-button">{savingPrefs && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}Save settings</Button>
              <Button variant="secondary" onClick={sendTest} disabled={testing} data-testid="settings-briefing-test-button">{testing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}Send a test to me</Button>
            </div>
          )}
        </div>
      </Panel>
      <Panel title="Delivery" icon={Clock} className="lg:col-span-5 h-fit" testid="settings-briefing-delivery">
        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground"><CheckCircle2 className="h-4 w-4 text-emerald-500" />Powered by Emergent Managed Email (Resend)</div>
          <div>
            <p className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Recipients</p>
            {data.recipients?.length ? (
              <ul className="space-y-1">
                {data.recipients.map((e) => <li key={e} className="text-sm font-mono bg-muted/50 rounded px-2 py-1 inline-block mr-1 mb-1">{e}</li>)}
              </ul>
            ) : <p className="text-sm text-muted-foreground">No eligible admins yet.</p>}
          </div>
          {data.eligible && (
            <div className="flex items-center justify-between pt-2 border-t border-border">
              <div><p className="text-sm font-medium">I want to receive it</p><p className="text-xs text-muted-foreground">Toggle to opt out personally.</p></div>
              <Switch checked={data.receive} onCheckedChange={setMyPref} data-testid="settings-briefing-mine-switch" />
            </div>
          )}
          {data.last && (
            <div className="pt-2 border-t border-border text-xs text-muted-foreground">
              Last sent: {data.last.date} &middot; {data.last.sent || 0} email{(data.last.sent || 0) === 1 ? "" : "s"} &middot; <span className="capitalize">{data.last.status}</span>
            </div>
          )}
        </div>
      </Panel>
    </div>
  );
}

function BillingTab() {
  const { user, refresh } = useAuth();
  const admin = isAdmin(user);
  const [sub, setSub] = useState(null);
  const [plans, setPlans] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([api.get("/billing/subscription"), api.get("/billing/plans")]);
      setSub(s.data); setPlans(p.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const subscribe = async (lookup_key) => {
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/billing/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(""); }
  };
  const openPortal = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(errMsg(e)); setBusy(""); }
  };

  if (!sub || !plans) return <div className="p-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  const statusLabel = { trialing: "Free trial", active: "Active", granted_free: "Granted free", past_due: "Past due", canceled: "Canceled", trial_expired: "Trial ended" }[sub.status] || sub.status;
  const statusCls = { active: "bg-emerald-100 text-emerald-900", trialing: "bg-sky-100 text-sky-900", granted_free: "bg-violet-100 text-violet-900", past_due: "bg-amber-100 text-amber-900", canceled: "bg-rose-100 text-rose-900", trial_expired: "bg-rose-100 text-rose-900" }[sub.status] || "bg-muted";

  return (
    <div className="space-y-5">
      <Panel title="Your subscription" icon={CreditCard} testid="billing-status-panel">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${statusCls}`} data-testid="billing-status-badge">{statusLabel}</span>
              <span className="text-sm font-medium" data-testid="billing-plan-name">{sub.plan_name || "No plan"}</span>
            </div>
            {sub.current_period_end && (
              <p className="text-xs text-muted-foreground mt-1.5">
                {sub.status === "trialing" ? "Trial ends" : sub.cancel_at_period_end ? "Cancels on" : "Renews on"} {fmtDateTime(sub.current_period_end)}
              </p>
            )}
          </div>
          {admin && sub.billing?.customer_id && (
            <Button variant="secondary" onClick={openPortal} disabled={busy === "portal"} data-testid="billing-portal-button">
              {busy === "portal" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Cog className="h-4 w-4 mr-1.5" />}
              Manage billing in Stripe
            </Button>
          )}
        </div>
      </Panel>
      <Panel title="Plans" icon={Sparkles} testid="billing-plans-panel">
        {plans.length === 0 ? (
          <p className="text-sm text-muted-foreground">No plans available yet. Please check back soon.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {plans.map((p) => (
              <div key={p.plan_id} className="border border-border rounded-xl p-5 flex flex-col" data-testid="billing-plan-card">
                <div><p className="font-display font-semibold text-lg">{p.name}</p><p className="text-xs text-muted-foreground min-h-[2rem]">{p.description}</p></div>
                <ul className="mt-3 space-y-1 flex-1">
                  {(p.features || []).slice(0, 6).map((f) => (<li key={f} className="text-sm flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />{f}</li>))}
                </ul>
                <div className="mt-4 space-y-2">
                  {(p.prices || []).map((price) => (
                    <Button key={price.lookup_key} className="w-full justify-between" variant={sub.plan_id === p.plan_id ? "secondary" : "default"}
                      onClick={() => admin && subscribe(price.lookup_key)} disabled={!admin || busy === price.lookup_key}
                      data-testid={`billing-subscribe-${price.lookup_key}`}>
                      <span>{sub.plan_id === p.plan_id ? "Current" : price.interval ? `Subscribe ${price.interval}ly` : "Buy once"}</span>
                      <span className="font-semibold">{(price.currency || "usd").toUpperCase()} {Number(price.amount).toFixed(2)}{price.interval ? `/${price.interval[0]}` : ""}</span>
                    </Button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        {!admin && <p className="text-xs text-muted-foreground mt-4">Only owners and admins can change the subscription.</p>}
      </Panel>
    </div>
  );
}
